import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  input,
  viewChild,
} from '@angular/core';
import { getUVEState } from '@dotcms/uve';
import { Observable, Subject, takeUntil } from 'rxjs';

import { AccountFacade } from 'ish-core/facades/account.facade';
import { AppFacade } from 'ish-core/facades/app.facade';
import { DeviceType } from 'ish-core/models/viewtype/viewtype.types';
import { FeatureToggleService } from 'ish-core/utils/feature-toggle/feature-toggle.service';
import { PreviewService } from 'ish-core/utils/preview/preview.service';
import { SearchBoxComponent } from 'ish-shared/components/search/search-box/search-box.component.all';

type CollapsibleComponent = 'search' | 'navbar' | 'minibasket';

@Component({
  selector: 'custom-header-default',
  templateUrl: './custom-header-default.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./custom-header-default.component.scss'],
  standalone: false,
})
export class CustomHeaderDefaultComponent implements OnInit, OnChanges, OnDestroy {
  readonly isSticky = input(false);
  readonly deviceType = input<DeviceType>(undefined);
  readonly reset = input<unknown>(undefined);
  isLoggedIn$: Observable<boolean>;
  activeComponent: CollapsibleComponent = 'search';
  showSearchBox = false;
  showSignIn = false;
  isInsideUveEditor = !!getUVEState();
  readonly containerRef = viewChild<ElementRef>('globalNav');
  readonly searchBoxComponent = viewChild(SearchBoxComponent);
  readonly searchBoxWrapper = viewChild<ElementRef>('searchBoxWrapper');
  shouldFocusSearch = false;

  private destroy$ = new Subject<void>();

  constructor(
    private accountFacade: AccountFacade,
    private featureToggleService: FeatureToggleService,
    private appFacade: AppFacade,
    private previewService: PreviewService
  ) {}

  ngOnInit() {
    this.isLoggedIn$ = this.accountFacade.isLoggedIn$;
    this.subscribeToSearchBoxState();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.reset) {
      this.resetActiveComponent();
    }
    this.toggleSpecialStatusOfSearch();
    this.handleStickyHeaderScroll();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showSearch() {
    return this.activeComponent === 'search' && (this.deviceType() === 'mobile' || this.isSticky());
  }

  get showNavBar() {
    return this.activeComponent === 'navbar' || this.deviceType() === 'desktop';
  }

  toggle(component: CollapsibleComponent) {
    this.activeComponent = this.activeComponent === component ? this.getDefaultActiveComponent() : component;
  }

  isDotCmsEditView(): boolean {
    return this.featureToggleService.enabled('dotCmsUve') && !!this.isInsideUveEditor;
  }

  // emitdotcmsHomePageEditEvent() {
  //   if (this.isInsideIframe()) {
  //     emitDotCmsEMAEvent('/index');
  //   }
  // }

  // isInsideIframe(): boolean {
  //   return isSiteEmbedded();
  // }

  showOrHideSignIn(clickedOutside?: string) {
    this.showSignIn = clickedOutside ? false : !this.showSignIn;
    return this.showSignIn;
  }

  showOrHideSearchBox() {
    this.showSearchBox = !this.showSearchBox;

    if (this.showSearchBox && this.deviceType() !== 'desktop') {
      // Temporarily disable shouldFocusSearch to force re-evaluation
      this.shouldFocusSearch = false;

      requestAnimationFrame(() => {
        this.shouldFocusSearch = true;
      });
    } else {
      this.shouldFocusSearch = false;
    }
  }

  hideSearchBox() {
    this.showSearchBox = false;
    this.searchBoxComponent()?.blurSearchInput?.(); // optional
  }

  searchBoxActive() {
    this.showSearchBox = true;
  }

  getSearchBoxAnimationClass() {
    return {
      'search-active': this.showSearchBox,
      'search-inactive': !this.showSearchBox,
    };
  }

  getSignInAnimationClass() {
    return {
      'signin-active': this.showSignIn,
      'signin-inactive': !this.showSignIn,
    };
  }

  getHomePageUrl() {
    return this.isInsideUveEditor ? (this.previewService.isDesignViewMode ? '/home' : '/page/index') : '/home';
  }

  private subscribeToSearchBoxState() {
    this.appFacade
      .getSeachActive()
      .pipe(takeUntil(this.destroy$))
      .subscribe(active => (this.showSearchBox = active));
  }

  private resetActiveComponent() {
    this.activeComponent = 'search';
  }

  private toggleSpecialStatusOfSearch() {
    const isSticky = this.isSticky();
    if (isSticky && this.activeComponent === 'search') {
      this.activeComponent = undefined;
    }
    if (!isSticky && !this.activeComponent) {
      this.activeComponent = 'search';
    }
  }

  private handleStickyHeaderScroll() {
    const isSticky = this.isSticky();
    if (isSticky) {
      this.onScroll();
      this.addOrRemoveScrollListener(isSticky);
    }
  }

  private addOrRemoveScrollListener(add: boolean) {
    const method = add ? 'addEventListener' : 'removeEventListener';
    this.containerRef().nativeElement[method]('wheel', this.horizontalScrollHandler, { passive: false });
  }

  private getDefaultActiveComponent(): CollapsibleComponent {
    return this.isSticky() ? undefined : 'search';
  }

  private horizontalScrollHandler = (event: WheelEvent) => {
    if (this.shouldHandleHorizontalScroll(event)) {
      event.preventDefault();
      this.containerRef().nativeElement.scrollLeft += event.deltaY;
    }
  };

  private shouldHandleHorizontalScroll(event: WheelEvent): boolean {
    const isMouseWheel = Math.abs(event.deltaY) >= 50;
    const container = this.containerRef().nativeElement;
    return this.isSticky() && isMouseWheel && container.scrollWidth > container.clientWidth;
  }

  onScroll() {
    if (!SSR && this.deviceType() === 'desktop') {
      this.updateOverflowClasses();
    }
  }

  private updateOverflowClasses() {
    const container = this.containerRef()?.nativeElement;
    const globalNav = document.getElementById('globalNavigation');
    globalNav.classList.add('has-overflow-right', 'has-overflow-left');

    if (container.scrollLeft === 0) {
      globalNav.classList.remove('has-overflow-left');
    }
    if (container.scrollWidth - container.clientWidth - container.scrollLeft < 4) {
      globalNav.classList.remove('has-overflow-right');
    }
  }

  onCloseSearchBox(): void {
    if (this.deviceType() !== 'desktop') {
      this.searchBoxComponent()?.blurSearchInput?.();
      this.showSearchBox = false;
    }
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (this.deviceType() !== 'desktop') {
      const clickedInside = this.searchBoxWrapper()?.nativeElement.contains(event.target);
      const clickedToggle = (event.target as HTMLElement).closest('.search-toggle-icon');

      // If click is outside search box and not on the toggle button
      if (!clickedInside && !clickedToggle && this.showSearchBox) {
        this.onCloseSearchBox();
      }
    }
  }
}
